import Map from 'ol/Map';
import { Style } from 'ol/style';
import { Coordinate } from 'ol/coordinate';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';

import MikeVisualizer2DMeasureUtil from './MikeVisualizer2DMeasureUtil';
import MikeVisualizer2DMeasureCore from './MikeVisualizer2DMeasureCore';
import MikeVisualizer2DMeasureInteractions from './MikeVisualizer2DMeasureInteractions';

const { _enableMeasureInteraction } = MikeVisualizer2DMeasureInteractions;
const { _getOrSetupOpenLayersMeasureMap } = MikeVisualizer2DMeasureCore;
const {
  _updateHelpTooltip,
  _updateMeasureTooltip,
  _createStaticMeasureTooltip,
  _setMeasurementUnit,
  getFormattedLength,
} = MikeVisualizer2DMeasureUtil;

/**
 * Allows enabling/disabling line measurements.
 *
 * @module MikeVisualize2DLineMeasure
 * @version 1.0.0
 */
const TOOLTIP_WHILE_DRAWING = 'Double-press to complete measurement';
const TOOLTIP_BEFORE_DRAWING = 'Press to start measuring';

/**
 * Enable 2D line measurements via line drawing.
 * This will take focus of the visualizer until disabled.
 *
 * @param [drawInteractionStyle]
 * @param [vectorLayerStyle]
 * @param [clearFeaturesOnStart] Remove previous measurements when starting to make a new measurement.
 */
const enable2DLineMeasure = async (
  drawInteractionStyle?: (feature) => Array<Style>,
  vectorLayerStyle?: (feature) => Array<Style>,
  clearFeaturesOnStart?: boolean
) => {
  const measureMap = (await _getOrSetupOpenLayersMeasureMap()) as Map;

  const onUpdateHelpTooltip = (position: Coordinate, isSomethingSketched: boolean) => {
    const tooltipMessage = isSomethingSketched ? TOOLTIP_WHILE_DRAWING : TOOLTIP_BEFORE_DRAWING;

    _updateHelpTooltip(tooltipMessage, position);
  };

  const onUpdateMeasureTooltip = (geometry: LineString | Polygon) => {
    const tooltipCoord = geometry.getLastCoordinate();
    const geom = geometry as LineString;
    _updateMeasureTooltip(getFormattedLength(geom), tooltipCoord);
  };

  const onCreateStaticMeasureTooltip = (geometry: LineString | Polygon) => {
    const geom = geometry as LineString;
    _createStaticMeasureTooltip(getFormattedLength(geom), geometry.getLastCoordinate());
  };

  try {
    await _enableMeasureInteraction(
      measureMap,
      'LineString',
      drawInteractionStyle,
      vectorLayerStyle,
      clearFeaturesOnStart,
      onUpdateHelpTooltip,
      onUpdateMeasureTooltip,
      onCreateStaticMeasureTooltip
    );

    return true;
  } catch (error) {
    console.error('Failed to enable 2d line measure', error);
    return false;
  }
};

const setLineMeasurementUnit = (unit: string) => {
  _setMeasurementUnit(unit)
}

const self = {
  enable2DLineMeasure,
  setLineMeasurementUnit,
};

export default self;
